import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  Trash2,
  Pencil,
  Plus,
  ChevronsUpDown,
  Check,
  Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Toaster, toast } from "sonner";
import { useSessionState } from "@/hooks/useSessionState";
import {
  listCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon as deleteCouponApi,
} from "@/services/couponsApi";
import type { SourceCoupon } from "@/services/couponsApi";
import { listSourcesAdmin, patchSource } from "@/services/sourcesApi";
import type {
  SourceWithStatsAdmin,
  SourceUpdateRequest,
} from "@/services/sourcesApi";
import { searchProductsByName } from "@/services/productsApi";
import { ApiError } from "@/services/api";
import type { SourceCouponsSessionState } from "@/types/pageState";

// ─── Query keys ───────────────────────────────────────────────────────────────

const couponKeys = {
  all: ["coupons"] as const,
  list: (sourceId?: string | null) =>
    [...couponKeys.all, "list", sourceId ?? "all"] as const,
};

const adminSourcesKeys = {
  all: ["sources", "admin"] as const,
  list: () => [...adminSourcesKeys.all, "list"] as const,
};

// ─── Date helpers (mirrors FeaturedSources) ───────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function localToIso(value: string): string | null {
  if (!value) return null;
  return `${value}:00Z`;
}

function isoToLocal(iso: string | null): string {
  if (!iso) return "";
  return iso.slice(0, 16);
}

// ─── Form state types ─────────────────────────────────────────────────────────

interface CouponFormState {
  sourceId: string;
  scope: "sitewide" | "per-product";
  productId: number | null;
  productLabel: string;
  code: string;
  label: string;
  description: string;
  discountPercent: string;
  startDate: string;
  endDate: string;
}

interface AffiliateFormState {
  program: string;
  urlTemplate: string;
  commissionPercent: string;
}

const defaultCouponForm: CouponFormState = {
  sourceId: "",
  scope: "sitewide",
  productId: null,
  productLabel: "",
  code: "",
  label: "",
  description: "",
  discountPercent: "",
  startDate: "",
  endDate: "",
};

const defaultAffiliateForm: AffiliateFormState = {
  program: "",
  urlTemplate: "",
  commissionPercent: "",
};

// ─── Page component ───────────────────────────────────────────────────────────

export default function Coupons() {
  const queryClient = useQueryClient();

  const [sessionState, setSessionState] =
    useSessionState<SourceCouponsSessionState>({
      key: "source-coupons-ui",
      defaultValue: {
        addDialogOpen: false,
        editId: null,
        deleteId: null,
        affiliateSourceId: null,
        filterSourceId: null,
      },
    });

  const [couponForm, setCouponForm] =
    useState<CouponFormState>(defaultCouponForm);
  const [affiliateForm, setAffiliateForm] =
    useState<AffiliateFormState>(defaultAffiliateForm);

  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [filterPickerOpen, setFilterPickerOpen] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [debouncedProductQuery, setDebouncedProductQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedProductQuery(productQuery), 300);
    return () => clearTimeout(timer);
  }, [productQuery]);

  // ─── Queries ───────────────────────────────────────────────────────────────

  const { data: couponsData = [], isLoading: couponsLoading } = useQuery({
    queryKey: couponKeys.list(sessionState.filterSourceId),
    queryFn: () =>
      listCoupons(
        sessionState.filterSourceId
          ? { source_id: sessionState.filterSourceId }
          : undefined,
      ),
  });

  const { data: adminSourcesData, isLoading: sourcesLoading } = useQuery({
    queryKey: adminSourcesKeys.list(),
    queryFn: () => listSourcesAdmin(),
  });

  const allAdminSources = useMemo(
    () => adminSourcesData?.sources ?? [],
    [adminSourcesData],
  );

  const adminSourceById = useMemo(() => {
    const map: Record<string, SourceWithStatsAdmin> = {};
    for (const s of allAdminSources) map[s.id] = s;
    return map;
  }, [allAdminSources]);

  const sourceNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of allAdminSources) map[s.id] = s.name;
    return map;
  }, [allAdminSources]);

  const { data: productResults = [] } = useQuery({
    queryKey: [
      "products",
      "search-name",
      debouncedProductQuery,
      couponForm.sourceId,
    ],
    queryFn: () =>
      searchProductsByName(debouncedProductQuery, {
        source: couponForm.sourceId,
        limit: 20,
      }),
    enabled: debouncedProductQuery.length >= 2 && couponForm.sourceId !== "",
  });

  // ─── Derived state ─────────────────────────────────────────────────────────

  const couponsBySource = useMemo(() => {
    const map: Record<string, SourceCoupon[]> = {};
    for (const c of couponsData) {
      if (!map[c.source_id]) map[c.source_id] = [];
      map[c.source_id]!.push(c);
    }
    // When filtered, always show the source card even with no coupons.
    if (sessionState.filterSourceId && !map[sessionState.filterSourceId]) {
      map[sessionState.filterSourceId] = [];
    }
    return map;
  }, [couponsData, sessionState.filterSourceId]);

  const sourceIdsWithCards = useMemo(
    () => Object.keys(couponsBySource),
    [couponsBySource],
  );

  const editCoupon = useMemo(
    () =>
      sessionState.editId != null
        ? (couponsData.find((c) => c.id === sessionState.editId) ?? null)
        : null,
    [sessionState.editId, couponsData],
  );

  const couponToDelete = useMemo(
    () =>
      sessionState.deleteId != null
        ? (couponsData.find((c) => c.id === sessionState.deleteId) ?? null)
        : null,
    [sessionState.deleteId, couponsData],
  );

  // ─── Mutations ─────────────────────────────────────────────────────────────

  const addMutation = useMutation({
    mutationFn: () =>
      createCoupon({
        source_id: couponForm.sourceId,
        product_id:
          couponForm.scope === "per-product" ? couponForm.productId : null,
        code: couponForm.code.trim(),
        label: couponForm.label.trim(),
        description: couponForm.description.trim() || null,
        discount_percent: couponForm.discountPercent || null,
        start_date: localToIso(couponForm.startDate),
        end_date: localToIso(couponForm.endDate),
      }),
    onSuccess: () => {
      toast.success(`Coupon "${couponForm.code}" created.`);
      queryClient.invalidateQueries({ queryKey: couponKeys.all });
      setSessionState((prev) => ({ ...prev, addDialogOpen: false }));
      setCouponForm(defaultCouponForm);
    },
    onError: (err) => {
      if (
        err instanceof ApiError &&
        (err.status === 409 || err.status === 422)
      ) {
        toast.warning(err.message);
      } else {
        toast.error(
          err instanceof Error ? err.message : "Failed to create coupon.",
        );
      }
    },
  });

  const editMutation = useMutation({
    mutationFn: () =>
      updateCoupon(sessionState.editId!, {
        code: couponForm.code.trim(),
        label: couponForm.label.trim(),
        description: couponForm.description.trim() || null,
        discount_percent: couponForm.discountPercent || null,
        start_date: localToIso(couponForm.startDate),
        end_date: localToIso(couponForm.endDate),
      }),
    onSuccess: () => {
      toast.success("Coupon updated.");
      queryClient.invalidateQueries({ queryKey: couponKeys.all });
      setSessionState((prev) => ({ ...prev, editId: null }));
      setCouponForm(defaultCouponForm);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 422) {
        toast.warning(err.message);
      } else {
        toast.error(
          err instanceof Error ? err.message : "Failed to update coupon.",
        );
      }
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => deleteCouponApi(id),
    onSuccess: (_, id) => {
      const c = couponsData.find((x) => x.id === id);
      toast.success(`Coupon "${c?.code ?? id}" deleted.`);
      queryClient.invalidateQueries({ queryKey: couponKeys.all });
      setSessionState((prev) => ({ ...prev, deleteId: null }));
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete coupon.",
      );
    },
  });

  const affiliateMutation = useMutation({
    mutationFn: () => {
      const sourceId = sessionState.affiliateSourceId!;
      const patch: SourceUpdateRequest = {
        affiliate_program: affiliateForm.program.trim() || null,
        affiliate_url_template: affiliateForm.urlTemplate.trim() || null,
        affiliate_commission_percent: affiliateForm.commissionPercent
          ? parseFloat(affiliateForm.commissionPercent)
          : null,
      };
      return patchSource(sourceId, patch);
    },
    onSuccess: () => {
      toast.success("Affiliate settings updated.");
      queryClient.invalidateQueries({ queryKey: adminSourcesKeys.all });
      setSessionState((prev) => ({ ...prev, affiliateSourceId: null }));
      setAffiliateForm(defaultAffiliateForm);
    },
    onError: (err) => {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to update affiliate settings.",
      );
    },
  });

  // ─── Dialog openers ────────────────────────────────────────────────────────

  function openAddDialog() {
    setCouponForm(defaultCouponForm);
    setProductQuery("");
    setDebouncedProductQuery("");
    setSessionState((prev) => ({ ...prev, addDialogOpen: true }));
  }

  function openEditDialog(coupon: SourceCoupon) {
    setCouponForm({
      sourceId: coupon.source_id,
      scope: coupon.product_id != null ? "per-product" : "sitewide",
      productId: coupon.product_id,
      productLabel:
        coupon.product_id != null ? `Product #${coupon.product_id}` : "",
      code: coupon.code,
      label: coupon.label,
      description: coupon.description ?? "",
      discountPercent: coupon.discount_percent ?? "",
      startDate: isoToLocal(coupon.start_date),
      endDate: isoToLocal(coupon.end_date),
    });
    setSessionState((prev) => ({ ...prev, editId: coupon.id }));
  }

  function openDeleteDialog(coupon: SourceCoupon) {
    setSessionState((prev) => ({ ...prev, deleteId: coupon.id }));
  }

  function openAffiliateDialog(sourceId: string) {
    const src = adminSourceById[sourceId];
    setAffiliateForm({
      program: src?.affiliate_program ?? "",
      urlTemplate: src?.affiliate_url_template ?? "",
      commissionPercent: src?.affiliate_commission_percent ?? "",
    });
    setSessionState((prev) => ({ ...prev, affiliateSourceId: sourceId }));
  }

  function canSubmitCoupon(): boolean {
    if (
      !couponForm.sourceId ||
      !couponForm.code.trim() ||
      !couponForm.label.trim()
    ) {
      return false;
    }
    if (couponForm.scope === "per-product" && couponForm.productId == null) {
      return false;
    }
    if (couponForm.discountPercent) {
      const n = parseFloat(couponForm.discountPercent);
      if (isNaN(n) || n < 0 || n > 100) return false;
    }
    return true;
  }

  const isLoading = couponsLoading || sourcesLoading;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Toaster position="top-right" theme="dark" />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Coupons</h2>
          <Button onClick={openAddDialog} size="sm">
            <Plus className="size-4" />
            Add Coupon
          </Button>
        </div>

        {/* Source filter */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            Filter by source:
          </span>
          <Popover open={filterPickerOpen} onOpenChange={setFilterPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                role="combobox"
                className="w-60 justify-between font-normal"
              >
                {sessionState.filterSourceId
                  ? (sourceNameById[sessionState.filterSourceId] ??
                    sessionState.filterSourceId)
                  : "All sources"}
                <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <Command>
                <CommandInput placeholder="Search sources…" />
                <CommandList>
                  <CommandEmpty>No sources found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="__all__"
                      onSelect={() => {
                        setSessionState((prev) => ({
                          ...prev,
                          filterSourceId: null,
                        }));
                        setFilterPickerOpen(false);
                      }}
                    >
                      <Check
                        className={`mr-2 size-4 ${sessionState.filterSourceId == null ? "opacity-100" : "opacity-0"}`}
                      />
                      All sources
                    </CommandItem>
                    {allAdminSources.map((s) => (
                      <CommandItem
                        key={s.id}
                        value={`${s.name} ${s.id}`}
                        onSelect={() => {
                          setSessionState((prev) => ({
                            ...prev,
                            filterSourceId: s.id,
                          }));
                          setFilterPickerOpen(false);
                        }}
                      >
                        <Check
                          className={`mr-2 size-4 ${sessionState.filterSourceId === s.id ? "opacity-100" : "opacity-0"}`}
                        />
                        <span>{s.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground font-mono">
                          {s.id}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {sessionState.filterSourceId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setSessionState((prev) => ({
                  ...prev,
                  filterSourceId: null,
                }))
              }
            >
              Clear
            </Button>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="size-5 animate-spin" />
            Loading…
          </div>
        )}

        {!isLoading && sourceIdsWithCards.length === 0 && (
          <div className="text-sm text-muted-foreground py-8">
            No coupons found.
            {sessionState.filterSourceId && " Try clearing the filter."}
          </div>
        )}

        {!isLoading && (
          <div className="space-y-4">
            {sourceIdsWithCards.map((sourceId) => (
              <SourceCouponCard
                key={sourceId}
                sourceId={sourceId}
                sourceName={sourceNameById[sourceId] ?? sourceId}
                source={adminSourceById[sourceId]}
                coupons={couponsBySource[sourceId] ?? []}
                onEditCoupon={openEditDialog}
                onDeleteCoupon={openDeleteDialog}
                onEditAffiliate={openAffiliateDialog}
                deletePending={removeMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add coupon dialog */}
      <Dialog
        open={sessionState.addDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, addDialogOpen: open }))
        }
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Coupon</DialogTitle>
            <DialogDescription>
              Create a sitewide or per-product coupon code.
            </DialogDescription>
          </DialogHeader>
          <CouponForm
            form={couponForm}
            onChange={setCouponForm}
            sources={allAdminSources}
            sourceNameById={sourceNameById}
            sourcePickerOpen={sourcePickerOpen}
            onSourcePickerOpenChange={setSourcePickerOpen}
            showSourcePicker
            productPickerOpen={productPickerOpen}
            onProductPickerOpenChange={setProductPickerOpen}
            productQuery={productQuery}
            onProductQueryChange={setProductQuery}
            productResults={productResults}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({ ...prev, addDialogOpen: false }))
              }
            >
              Cancel
            </Button>
            <Button
              disabled={!canSubmitCoupon() || addMutation.isPending}
              onClick={() => addMutation.mutate()}
            >
              {addMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Add"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit coupon dialog */}
      <Dialog
        open={sessionState.editId != null}
        onOpenChange={(open) => {
          if (!open) setSessionState((prev) => ({ ...prev, editId: null }));
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Coupon</DialogTitle>
            <DialogDescription>
              {editCoupon
                ? `Editing "${editCoupon.code}" for ${sourceNameById[editCoupon.source_id] ?? editCoupon.source_id}`
                : "Edit coupon details."}
            </DialogDescription>
          </DialogHeader>
          <CouponForm
            form={couponForm}
            onChange={setCouponForm}
            sources={allAdminSources}
            sourceNameById={sourceNameById}
            sourcePickerOpen={false}
            onSourcePickerOpenChange={() => {}}
            showSourcePicker={false}
            productPickerOpen={false}
            onProductPickerOpenChange={() => {}}
            productQuery=""
            onProductQueryChange={() => {}}
            productResults={[]}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({ ...prev, editId: null }))
              }
            >
              Cancel
            </Button>
            <Button
              disabled={!canSubmitCoupon() || editMutation.isPending}
              onClick={() => editMutation.mutate()}
            >
              {editMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={sessionState.deleteId != null}
        onOpenChange={(open) => {
          if (!open) setSessionState((prev) => ({ ...prev, deleteId: null }));
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Coupon</DialogTitle>
            <DialogDescription>
              Delete coupon{" "}
              <strong>"{couponToDelete?.code ?? "this coupon"}"</strong>? This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({ ...prev, deleteId: null }))
              }
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={removeMutation.isPending}
              onClick={() => {
                if (sessionState.deleteId != null) {
                  removeMutation.mutate(sessionState.deleteId);
                }
              }}
            >
              {removeMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Affiliate settings dialog */}
      <Dialog
        open={sessionState.affiliateSourceId != null}
        onOpenChange={(open) => {
          if (!open)
            setSessionState((prev) => ({ ...prev, affiliateSourceId: null }));
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Affiliate Settings</DialogTitle>
            <DialogDescription>
              {sessionState.affiliateSourceId
                ? `${sourceNameById[sessionState.affiliateSourceId] ?? sessionState.affiliateSourceId}`
                : "Edit affiliate settings."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="aff-program">Program</Label>
              <Input
                id="aff-program"
                placeholder="e.g. Impact, ShareASale"
                value={affiliateForm.program}
                onChange={(e) =>
                  setAffiliateForm((prev) => ({
                    ...prev,
                    program: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="aff-url-template">URL Template</Label>
              <Input
                id="aff-url-template"
                placeholder="https://aff.example/?u={url}"
                value={affiliateForm.urlTemplate}
                onChange={(e) =>
                  setAffiliateForm((prev) => ({
                    ...prev,
                    urlTemplate: e.target.value,
                  }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Use {"{url}"} as a placeholder for the product URL. Leave empty
                to clear.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="aff-commission">Commission %</Label>
              <Input
                id="aff-commission"
                type="number"
                min={0}
                max={100}
                step={0.01}
                placeholder="e.g. 2.50"
                value={affiliateForm.commissionPercent}
                onChange={(e) =>
                  setAffiliateForm((prev) => ({
                    ...prev,
                    commissionPercent: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({
                  ...prev,
                  affiliateSourceId: null,
                }))
              }
            >
              Cancel
            </Button>
            <Button
              disabled={affiliateMutation.isPending}
              onClick={() => affiliateMutation.mutate()}
            >
              {affiliateMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Source coupon card ────────────────────────────────────────────────────────

interface SourceCouponCardProps {
  sourceId: string;
  sourceName: string;
  source: SourceWithStatsAdmin | undefined;
  coupons: SourceCoupon[];
  onEditCoupon: (coupon: SourceCoupon) => void;
  onDeleteCoupon: (coupon: SourceCoupon) => void;
  onEditAffiliate: (sourceId: string) => void;
  deletePending: boolean;
}

function SourceCouponCard({
  sourceId,
  sourceName,
  source,
  coupons,
  onEditCoupon,
  onDeleteCoupon,
  onEditAffiliate,
  deletePending,
}: SourceCouponCardProps) {
  const hasAffiliateSettings =
    source?.affiliate_program ||
    source?.affiliate_commission_percent ||
    source?.affiliate_url_template;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold">{sourceName}</div>
            <div className="text-xs text-muted-foreground font-mono font-normal">
              {sourceId}
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="text-xs text-muted-foreground text-right space-y-0.5 font-normal mt-0.5">
              {source?.affiliate_program && (
                <div>
                  <span className="text-foreground/60">Program:</span>{" "}
                  {source.affiliate_program}
                </div>
              )}
              {source?.affiliate_commission_percent && (
                <div>
                  <span className="text-foreground/60">Commission:</span>{" "}
                  {source.affiliate_commission_percent}%
                </div>
              )}
              {source?.affiliate_url_template && (
                <div
                  className="truncate max-w-48"
                  title={source.affiliate_url_template}
                >
                  <span className="text-foreground/60">URL:</span>{" "}
                  {source.affiliate_url_template}
                </div>
              )}
              {!hasAffiliateSettings && (
                <div className="text-muted-foreground/50">
                  No affiliate settings
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEditAffiliate(sourceId)}
              title="Edit affiliate settings"
            >
              <Pencil className="size-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {coupons.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No coupons for this source.
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {coupons.map((coupon) => (
              <li
                key={coupon.id}
                className="flex items-center gap-3 px-3 py-2.5"
              >
                <Tag className="size-4 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-medium text-sm">
                      {coupon.code}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {coupon.label}
                    </span>
                    {coupon.product_id != null ? (
                      <Badge variant="outline" className="text-xs">
                        Product #{coupon.product_id}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        Sitewide
                      </Badge>
                    )}
                    {coupon.discount_percent && (
                      <Badge className="text-xs">
                        {coupon.discount_percent}% off
                      </Badge>
                    )}
                  </div>
                  {(coupon.start_date || coupon.end_date) && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {formatDate(coupon.start_date)} &rarr;{" "}
                      {formatDate(coupon.end_date)}
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={() => onEditCoupon(coupon)}
                  title="Edit coupon"
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={deletePending}
                  onClick={() => onDeleteCoupon(coupon)}
                  title="Delete coupon"
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Coupon form (shared between add / edit dialogs) ──────────────────────────

interface SourceOption {
  id: string;
  name: string;
}

interface CouponFormProps {
  form: CouponFormState;
  onChange: (form: CouponFormState) => void;
  sources: SourceOption[];
  sourceNameById: Record<string, string>;
  sourcePickerOpen: boolean;
  onSourcePickerOpenChange: (open: boolean) => void;
  showSourcePicker: boolean;
  productPickerOpen: boolean;
  onProductPickerOpenChange: (open: boolean) => void;
  productQuery: string;
  onProductQueryChange: (q: string) => void;
  productResults: Array<{ details: { id: number | null; name: string } }>;
}

function CouponForm({
  form,
  onChange,
  sources,
  sourceNameById,
  sourcePickerOpen,
  onSourcePickerOpenChange,
  showSourcePicker,
  productPickerOpen,
  onProductPickerOpenChange,
  productQuery,
  onProductQueryChange,
  productResults,
}: CouponFormProps) {
  return (
    <div className="space-y-4 py-2">
      {/* Source picker */}
      {showSourcePicker && (
        <div className="space-y-2">
          <Label>Source</Label>
          <Popover
            open={sourcePickerOpen}
            onOpenChange={onSourcePickerOpenChange}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={sourcePickerOpen}
                className="w-full justify-between font-normal"
              >
                {form.sourceId
                  ? (sourceNameById[form.sourceId] ?? form.sourceId)
                  : "Select a source…"}
                <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder="Search sources…" />
                <CommandList>
                  <CommandEmpty>No sources found.</CommandEmpty>
                  <CommandGroup>
                    {sources.map((s) => (
                      <CommandItem
                        key={s.id}
                        value={`${s.name} ${s.id}`}
                        onSelect={() => {
                          onChange({
                            ...form,
                            sourceId: s.id,
                            productId: null,
                            productLabel: "",
                          });
                          onSourcePickerOpenChange(false);
                        }}
                      >
                        <Check
                          className={`mr-2 size-4 ${form.sourceId === s.id ? "opacity-100" : "opacity-0"}`}
                        />
                        <span>{s.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground font-mono">
                          {s.id}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Scope toggle */}
      <div className="space-y-2">
        <Label>Scope</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={form.scope === "sitewide" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              onChange({
                ...form,
                scope: "sitewide",
                productId: null,
                productLabel: "",
              })
            }
          >
            Sitewide
          </Button>
          <Button
            type="button"
            variant={form.scope === "per-product" ? "default" : "outline"}
            size="sm"
            disabled={!form.sourceId && showSourcePicker}
            onClick={() => onChange({ ...form, scope: "per-product" })}
          >
            Per-product
          </Button>
        </div>
      </div>

      {/* Product picker — only when per-product scope */}
      {form.scope === "per-product" && (
        <div className="space-y-2">
          <Label>Product</Label>
          <Popover
            open={productPickerOpen}
            onOpenChange={onProductPickerOpenChange}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={productPickerOpen}
                className="w-full justify-between font-normal"
              >
                {form.productLabel ||
                  (form.productId != null
                    ? `Product #${form.productId}`
                    : "Search for a product…")}
                <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Type to search products…"
                  value={productQuery}
                  onValueChange={onProductQueryChange}
                />
                <CommandList>
                  {productQuery.length < 2 ? (
                    <CommandEmpty>
                      Type at least 2 characters to search.
                    </CommandEmpty>
                  ) : productResults.length === 0 ? (
                    <CommandEmpty>No products found.</CommandEmpty>
                  ) : (
                    <CommandGroup>
                      {productResults.map((p) => {
                        const pid = p.details.id;
                        if (pid == null) return null;
                        return (
                          <CommandItem
                            key={pid}
                            value={`${p.details.name} ${pid}`}
                            onSelect={() => {
                              onChange({
                                ...form,
                                productId: pid,
                                productLabel: p.details.name,
                              });
                              onProductPickerOpenChange(false);
                            }}
                          >
                            <Check
                              className={`mr-2 size-4 ${form.productId === pid ? "opacity-100" : "opacity-0"}`}
                            />
                            <span>{p.details.name}</span>
                            <span className="ml-2 text-xs text-muted-foreground font-mono">
                              #{pid}
                            </span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Code + discount */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="coupon-code">Code</Label>
          <Input
            id="coupon-code"
            placeholder="e.g. SAVE10"
            value={form.code}
            onChange={(e) => onChange({ ...form, code: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="coupon-discount">Discount %</Label>
          <Input
            id="coupon-discount"
            type="number"
            min={0}
            max={100}
            step={0.01}
            placeholder="0–100"
            value={form.discountPercent}
            onChange={(e) =>
              onChange({ ...form, discountPercent: e.target.value })
            }
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="coupon-label">Label</Label>
        <Input
          id="coupon-label"
          placeholder="e.g. 10% off all orders"
          value={form.label}
          onChange={(e) => onChange({ ...form, label: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="coupon-description">Description</Label>
        <Textarea
          id="coupon-description"
          placeholder="Optional description"
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
          rows={2}
        />
      </div>

      {/* Date range */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="coupon-start">Start date</Label>
          <Input
            id="coupon-start"
            type="datetime-local"
            value={form.startDate}
            onChange={(e) => onChange({ ...form, startDate: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="coupon-end">End date</Label>
          <Input
            id="coupon-end"
            type="datetime-local"
            value={form.endDate}
            onChange={(e) => onChange({ ...form, endDate: e.target.value })}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Leave dates empty for an open-ended coupon.
      </p>
    </div>
  );
}
